import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useConfirm } from '@components/ConfirmProvider'
import api, { setAccessToken, clearAccessToken } from '@api/axios';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { alert, confirm } = useConfirm();


  // ✅ 추가
  const MAIN_PATH = '/';
  const logoutAlertLockRef = useRef(false); // 중복 alert 방지

  // ✅ 추가: 전역 강제 로그아웃 이벤트 수신
  // effect가 alert를 캡처(클로저로 잡음)한 상태로 고정되지 않게, 
  // alert가 바뀌면 effect를 다시 실행해서 이벤트 핸들러도 최신 alert를 쓰게 하려는 목적
  useEffect(() => {
    const handler = async (e) => {
      // 이미 처리 중이면 무시
      if (logoutAlertLockRef.current) return;
      logoutAlertLockRef.current = true;

      clearAccessToken();
      setUser(null);

      // 메인 페이지에서는 alert/redirect 반복하지 않기
      const isMainPage = window.location.pathname === MAIN_PATH;
      if (!isMainPage) {
        await alert('세션이 만료되었습니다. 다시 로그인해주세요.');
        window.location.replace(MAIN_PATH);
      }

      // 잠금 해제(너무 짧으면 또 뜰 수 있어 1~3초 권장)
      setTimeout(() => {
        logoutAlertLockRef.current = false;
      }, 2000);
    };

    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, [alert]);  // effect 안에서 alert 함수를 사용하고 있기 때문
  
  

  const refreshMe = async () => {
    try {
      const { data } = await api.get('/knouUser/me'); // 서버가 access_token 검증 후 유저 정보 반환
      setUser(data);
    } catch {
      clearAccessToken();
      setUser(null);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await refreshMe(); // 최초 하이드레이션에서만 호출
      setLoading(false);
    })();
  }, []);


  // 편의 플래그 (페이지 가드에서 사용)
  const isAuthenticated = !!user;
  const isAuthReady = !loading && !!user;   // 인증 정보 준비 완료 & 로그인 상태

  const idpwLogin = async (memberEmail, memberPassword) => {
    try {
      const { data, status } = await api.post('/knouUser/auth/login', { memberEmail, memberPassword });
      if (data?.success) {
        const at = data?.data?.accessToken || data?.accessToken;
        if (at) setAccessToken(at);
        await refreshMe();
        return { ok: true };
      }

      // success=false 이지만 200으로 내려오는 경우
      return { 
        ok: false, 
        message: data?.message ?? '연결된 이메일 계정을 찾을 수 없습니다.', 
        status,
      };
    } catch (err) {
      const status = err?.response?.status;
      const apiData = err?.response?.data;

      const msg =
        apiData?.message ||      // 서버에서 내려준 에러 메시지
        err?.message ||          // Axios / 네트워크 메시지
        '로그인 중 오류가 발생했습니다';

      // ★ 423(휴면계정)인 경우 data 안에 accountId, accountIdSeq가 들어있음
      const inactiveInfo = status === 423 ? apiData?.data : undefined;

      return {
        ok: false,
        message: msg,
        status,
        inactiveInfo,   // <-- 휴면계정 정보 프론트로 넘기기
      };
    }
  };

  const snsLogin = async (payload) => {
    try {
      const { data, status } = await api.post('/knouUser/auth/sns-login', payload);
      if (data?.success) {
        const at = data?.data?.accessToken || data?.accessToken;
        if (at) setAccessToken(at);
        await refreshMe();
        return { ok: true };
      }

      // success=false 이지만 200으로 내려오는 경우
      return { 
        ok: false, 
        message: data?.message ?? '연결된 SNS계정을 찾을 수 없습니다.', 
        status,
      };
    } catch (err) {
      const status = err?.response?.status;
      const apiData = err?.response?.data;

      const msg =
        apiData?.message ||      // 서버에서 내려준 에러 메시지
        err?.message ||          // Axios / 네트워크 메시지
        '로그인 중 오류가 발생했습니다';

      // ★ 423(휴면계정)인 경우 data 안에 accountId, accountIdSeq가 들어있음
      const inactiveInfo = status === 423 ? apiData?.data : undefined;

      return {
        ok: false,
        message: msg,
        status,
        inactiveInfo,   // <-- 휴면계정 정보 프론트로 넘기기
      };
    }
  };

  const logout = async () => {
    try { 
      const { data } = await api.post('/knouUser/auth/logout');
    } finally {
      clearAccessToken();
      setUser(null);
      // ✅ 추가
      window.location.replace(MAIN_PATH);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, isAuthReady, idpwLogin, snsLogin, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
